import { useState, createContext, useEffect } from 'react';
import { useModal } from 'src/services/modal';
import pagesHelpInfo from 'src/components/HelpInfo/PagesHelpInfo';
import Steps from 'src/components/steps/steps';
import { useHistory, useLocation } from 'react-router-dom';
const StepperContext = createContext();

export default StepperContext;

/**
 * StepperProvider is a component that provides context for managing the current step of a multi-step process.
 * It maintains a local state for the step number and provides functions for updating the step number. Additionally, it also handle the Help information modal using useModal Hook and pagesHelpInfo import.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {React.ReactElement|React.ReactElement[]} props.children - A React element or a list of React elements that will be rendered within the StepperContext.Provider component.
 *
 * @returns {React.ReactElement} - A React element that renders the StepperContext.Provider component with the value for the step context, and also for handling the Help information modal.
 */
export const StepperProvider = (props) => {
  const history = useHistory();
  const location = useLocation();

  const infoModalController = useModal();
  const [infoModalContent, setInfoModalContent] = useState(null);

  //initalize step with step number based on path
  const [stepNo, setStepNo] = useState(
    Steps.findIndex(({ path }) => {
      return path.includes(location.pathname);
    })
  );

  const [jumpToStep, setJumpToStep] = useState(null);

  //This is required because when the user interacts with the `WebStepper` component and clicks on a step button
  //rather than the next button, it needs to validate the current step.
  //The form reference is required to validate the form, and without it, the `WebStepper` component
  //would not be able to perform validation and submit the form.
  const [formikRef, setFormikRef] = useState(null);

  /**
   * init state object is used to determine if the component has been
   * initialized for the first time. This helps to avoid triggering the
   * useEffect hook on initial render, which would cause an unnecessary
   * push to the history object.
   */
  const [init, setInit] = useState(true);

  /**
   * useEffect hook that navigates to the first path in the current step.
   * @param {Step[]} Steps - An array of steps containing the path information.
   * @param {number} stepNo - The index of the current step.
   * @param {Object} history - The history object from react-router-dom.
   */
  useEffect(() => {
    if (init === false) {
      const step = Steps[stepNo];
      const path = step?.path[0];
      history.push(path);
    }
    setInit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepNo]);

  /**
   * increments the step number by incrementStepNoBy or set the step number to jumpToStep.
   * @param {Number} [incrementStepNoBy=1] number to be added to the step number
   * @returns {void}
   */
  const nextStep = (incrementStepNoBy = 1) => {
    /**
     * check if jumpToStep is null
     * if true, increment step number by incrementStepNoBy
     * if false, set step number to jumpToStep
     */
    jumpToStep === null
      ? setStepNo((prevStepNo) => prevStepNo + incrementStepNoBy)
      : setStepNo(jumpToStep);
    setJumpToStep(null);
  };

  const backStep = () => {
    setStepNo(stepNo - 1);
  };

  /**
   * Sets the step number based on the provided router path.
   * This updates the stepper UI to display the correct step
   * when the user navigates to a route that corresponds to a step in the process.
   *
   * @param {string} [routerPath=''] - The router path used to determine the step number
   */
  const setStepByPath = (routerPath = '') => {
    const index = Steps.findIndex(({ path }) => {
      return path.includes(routerPath);
    });
    setStepNo(index);
  };

  useEffect(() => {
    const content = pagesHelpInfo[stepNo];
    setInfoModalContent(content);
  }, [stepNo]);

  const contextValue = {
    nextStep,
    stepNo,
    setStepNo,
    backStep,
    infoModalController,
    infoModalContent,
    formikRef,
    setJumpToStep,
    setFormikRef,
    setStepByPath,
  };

  return <StepperContext.Provider value={contextValue}>{props.children}</StepperContext.Provider>;
};
